/**
 * 轻量反馈（表扬语 + 星星 + 音效）
 * ------------------------------------------------------------
 * 小学生需要即时、轻、不打断的反馈：
 *   - 答对：随机一句英文表扬（Good job! / Great! ...）+ 星星飞出的微动画 + 上升音效
 *   - 答错：不打击，给一句 Here's a hint 式的温柔提示，允许重试
 *
 * 音效用 WebAudio 合成，**不引入任何音频文件**，离线可用、不增加体积。
 * 浏览器要求首次音频必须由用户手势触发，我们在第一次点击时解锁 AudioContext。
 */
import { el } from '../utils/index.js';

const PRAISE_EN = ['Good job!', 'Great!', 'Well done!', 'Nice work!', 'Excellent!', 'Perfect!', 'Awesome!'];
const PRAISE_ZH = ['很好！', '太棒了！', '做得好！', '真厉害！', '完全正确！'];

let audioCtx = null;
let unlocked = false;

function ctx() {
  if (audioCtx) return audioCtx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { audioCtx = new AC(); } catch { return null; }
  return audioCtx;
}

/** 在首次用户手势里调用一次即可（避免"没有声音"的困惑） */
export function unlockAudio() {
  if (unlocked) return;
  unlocked = true;
  // 1) 解锁 WebAudio（音效）
  const c = ctx();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
  // 2) 解锁 speechSynthesis（TTS 朗读）—— iOS Safari / 部分安卓平板
  //    要求首次朗读在用户手势中触发，这里用一段静音音频"热身"，
  //    之后页面里任何 speak() 都能正常发声。
  try {
    if ('speechSynthesis' in window) {
      const warm = new SpeechSynthesisUtterance(' ');
      warm.volume = 0;
      speechSynthesis.speak(warm);
    }
  } catch { /* ignore */ }
}

/** 播放一个音符 */
function tone(freq, start, dur, gain = 0.06, type = 'sine') {
  const c = ctx();
  if (!c) return;
  const t0 = c.currentTime + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** 答对音效：上行三音（C-E-G），轻快不吵 */
export function playCorrectSound(enabled = true) {
  if (!enabled) return;
  tone(523.25, 0, 0.13);
  tone(659.25, 0.09, 0.14);
  tone(783.99, 0.18, 0.22, 0.055);
}

/** 答错音效：两声低音，柔和，不用刺耳蜂鸣 */
export function playWrongSound(enabled = true) {
  if (!enabled) return;
  tone(311.13, 0, 0.12, 0.05, 'triangle');
  tone(246.94, 0.11, 0.18, 0.045, 'triangle');
}

/** 升级/解锁音效 */
export function playFanfare(enabled = true) {
  if (!enabled) return;
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, i * 0.11, 0.26, 0.055));
}

/**
 * 在某个元素位置飘出星星
 * @param {HTMLElement} anchor 以它的中心为起点
 * @param {number} n 星星数量
 */
export function burstStars(anchor, n = 5) {
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  const host = el('div', { class: 'star-burst', style: {
    left: `${rect.left + rect.width / 2}px`,
    top: `${rect.top + rect.height / 2}px`,
  } });
  document.body.appendChild(host);
  for (let i = 0; i < n; i++) {
    const s = el('span', { class: 'star-particle', text: '★' });
    const angle = (-Math.PI / 2) + (Math.random() - 0.5) * 2.2;
    const dist = 46 + Math.random() * 60;
    s.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
    s.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
    s.style.setProperty('--delay', `${i * 40}ms`);
    s.style.setProperty('--size', `${12 + Math.random() * 10}px`);
    host.appendChild(s);
  }
  setTimeout(() => host.remove(), 1100);
}

/**
 * 答对反馈条
 * @param {object} opts { el: 锚点元素, sound: boolean, streak: 连续答对次数 }
 * @returns {string} HTML
 */
export function praiseBar({ streak = 0, sound = true } = {}) {
  const en = PRAISE_EN[Math.floor(Math.random() * PRAISE_EN.length)];
  const zh = PRAISE_ZH[Math.floor(Math.random() * PRAISE_ZH.length)];
  const combo = streak >= 3 ? `<span class="combo">🔥 连对 ${streak}</span>` : '';
  return `<div class="praise-bar">
    <span class="praise-star">★</span>
    <span class="praise-en">${en}</span>
    <span class="praise-zh">${zh}</span>
    ${combo}
  </div>`;
}

/** 答错反馈条：温柔、给提示、鼓励重试 */
export function encourageBar({ canRetry = true, hint = '' } = {}) {
  return `<div class="encourage-bar">
    <span class="enc-icon">💪</span>
    <div class="enc-body">
      <b>${canRetry ? '再想想，你可以的！' : '这次没做对，记住它就够了'}</b>
      ${hint ? `<div class="enc-hint">💡 ${hint}</div>` : ''}
    </div>
  </div>`;
}

/** 分数气泡（+1 之类的小动画） */
export function scorePop(anchor, text = '+1') {
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  const b = el('div', { class: 'score-pop', text, style: {
    left: `${rect.left + rect.width / 2}px`,
    top: `${rect.top}px`,
  } });
  document.body.appendChild(b);
  setTimeout(() => b.remove(), 900);
}
